/**
 * Viraasat Parallax Engine — Single-Scene Scroll
 *
 * Scene 1 (Multi-layer city):     progress 0    → 0.58
 * Exit transition:                progress 0.58 → 1.00
 *
 * Performance: lerped scroll tracking, scroll-gated RAF,
 * cached viewport dims, batched DOM writes.
 */

(function () {
  'use strict';

  var container = document.querySelector('[data-parallax-container]');
  if (!container) return;

  /* ── Utilities ── */
  function clamp(val, min, max) {
    return val < min ? min : val > max ? max : val;
  }

  function mapRange(value, inMin, inMax, outMin, outMax) {
    var t = clamp((value - inMin) / (inMax - inMin), 0, 1);
    return outMin + t * (outMax - outMin);
  }

  function easeOutCubic(t) {
    return 1 - (1 - t) * (1 - t) * (1 - t);
  }

  /* Smooth interpolation — the core of buttery motion */
  function lerp(current, target, factor) {
    var diff = target - current;
    /* Snap when close enough to avoid sub-pixel jitter */
    if (diff < 0.5 && diff > -0.5) return target;
    return current + diff * factor;
  }

  /* ── Elements (cached once) ── */
  var bgLayer0       = document.getElementById('bgLayer0');
  var layer1         = document.getElementById('layer1');
  var layer2         = document.getElementById('layer2');
  var layer3         = document.getElementById('layer3');
  var scene1Fade     = document.getElementById('scene1Fade');
  var heroText       = document.getElementById('heroText');
  var transitionBar1 = document.getElementById('transitionBar1');
  var crestLogo      = document.getElementById('crestLogo');
  var scrollHint     = document.getElementById('scrollHint');
  var stickyCta      = document.querySelector('[data-waitlist-sticky]');

  /* ── Cached viewport dimensions ── */
  var vh = window.innerHeight;
  var vw = window.innerWidth;
  var isMobileView = vw <= 768;
  var isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  var scrollHeight = 0;
  var containerTop = 0;
  var waitlistSection = document.getElementById('waitlist');

  function recalculate() {
    vh = window.innerHeight;
    vw = window.innerWidth;
    isMobileView = vw <= 768;
    isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    containerTop = container.offsetTop;
    var spacer = container.querySelector('[data-parallax-spacer]');
    scrollHeight = spacer ? spacer.offsetHeight : (container.scrollHeight - vh);
    LERP_FACTOR = isCoarsePointer ? 0.18 : 0.14;
  }

  recalculate();

  /* Debounced resize — recalculate once settled */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(recalculate, 120);
  });

  /* ── CTA click handlers ── */
  var ctas = document.querySelectorAll('[data-waitlist-cta], [data-waitlist-sticky]');
  ctas.forEach(function (cta) {
    cta.addEventListener('click', function (e) {
      e.preventDefault();
      var target = document.getElementById('waitlist');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  });

  /* ══════════════════════════════════════════════
     LERPED SCROLL STATE
     Instead of reading scrollY directly, we lerp
     toward it. This creates buttery-smooth motion
     even when the browser delivers chunky scroll
     deltas (trackpads, mice, touch).
     ══════════════════════════════════════════════ */
  var currentScroll = window.scrollY;
  var targetScroll = currentScroll;
  var needsUpdate = true;
  var hasScrolled = false;

  /* Lerp factor: 0.12 = silky smooth, 0.2 = responsive.
     We use 0.14 as a balance — fast enough to not feel
     laggy, smooth enough to eliminate jank. */
  var LERP_FACTOR = isCoarsePointer ? 0.18 : 0.14;

  /* Track raw scroll position */
  window.addEventListener('scroll', function () {
    targetScroll = window.scrollY;
    needsUpdate = true;
  }, { passive: true });

  /* ══════════════════════════════════════════════
     SCROLL SNAP AT SCENE CHECKPOINTS
     Snaps once per checkpoint, then releases after
     a short cooldown so the user can continue.
     ══════════════════════════════════════════════ */
  var snapPoints = [0.12];
  var snappedAt = -1;
  var lastSnapProgress = 0;
  var snapCooldown = false;

  function getProgressFromScroll(scrollY) {
    if (!scrollHeight) return 0;
    return clamp((scrollY - containerTop) / scrollHeight, 0, 1);
  }

  function snapToProgress(p) {
    var targetY = containerTop + p * scrollHeight;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
    targetScroll = targetY;
    needsUpdate = true;
    /* Brief cooldown prevents re-triggering */
    snapCooldown = true;
    setTimeout(function () { snapCooldown = false; }, 600);
  }

  if (!isCoarsePointer) {
    window.addEventListener('wheel', function (e) {
      if (snapCooldown) return;
      var currentProgress = getProgressFromScroll(window.scrollY);

      if (e.deltaY > 0) {
        for (var i = 0; i < snapPoints.length; i++) {
          if (lastSnapProgress < snapPoints[i] && currentProgress >= snapPoints[i] && snappedAt !== i) {
            if (currentProgress - lastSnapProgress > 0.03) {
              e.preventDefault();
              snappedAt = i;
              snapToProgress(snapPoints[i]);
              lastSnapProgress = snapPoints[i];
              return;
            }
          }
        }
      }

      if (e.deltaY < 0 && snappedAt >= 0 && currentProgress < snapPoints[snappedAt]) {
        snappedAt = -1;
      }

      lastSnapProgress = currentProgress;
    }, { passive: false });
  }

  /* ══════════════════════════════════════════════
     PARALLAX RENDER — called only when needed
     ══════════════════════════════════════════════ */
  function render(relativeScroll) {
    if (!scrollHeight) return;
    var progress = clamp(relativeScroll / scrollHeight, 0, 1);
    var exitStart = isMobileView ? 0.46 : 0.58;
    var exitTravel = vh * (isMobileView ? 1.02 : 0.82);
    var heroFadeStart = isMobileView ? 0.42 : 0.56;
    var heroFadeEnd = isMobileView ? 0.58 : 0.72;

    /* Scroll hint dismiss */
    if (currentScroll > 50 && !hasScrolled) {
      hasScrolled = true;
      if (scrollHint) {
        scrollHint.classList.remove('visible');
        scrollHint.classList.add('fading');
      }
    }

    /* ═══════════════════════════════════════════
       SINGLE-SCENE CONTINUOUS SCROLL
       ═══════════════════════════════════════════ */

    var s1BaseY = mapRange(progress, exitStart, 1.00, 0, -exitTravel);

    /* ═══════════════════════════
       SCENE 1 (progress 0 → 1)
       ═══════════════════════════ */

    var l1Raw = mapRange(progress, 0.0, 0.45, 0, 1);
    var l1P = easeOutCubic(l1Raw);
    var l1Scale = 1.2 - l1P * 0.25;
    var l1Y = -relativeScroll * 0.02 + s1BaseY;
    layer1.style.transform = 'translate3d(0,' + l1Y + 'px,0) scale(' + l1Scale + ')';

    var l2Raw = mapRange(progress, 0.04, 0.30, 0, 1);
    var l2P = easeOutCubic(l2Raw);
    var l2EntryY = vh * 1.3 * (1 - l2P);
    var l2Scale = 1.25 - l2P * 0.3;
    var l2Opacity = mapRange(l2Raw, 0, 0.35, 0, 1);
    layer2.style.transform = 'translate3d(0,' + (l2EntryY + s1BaseY) + 'px,0) scale(' + l2Scale + ')';
    layer2.style.opacity = l2Opacity;

    var l3Raw = mapRange(progress, 0.08, 0.34, 0, 1);
    var l3P = easeOutCubic(l3Raw);
    var l3EntryY = vh * 1.4 * (1 - l3P);
    var l3Scale = 0.7 + l3P * 0.35;
    var l3Opacity = mapRange(l3Raw, 0, 0.3, 0, 1);
    layer3.style.transform = 'translate3d(0,' + (l3EntryY + s1BaseY) + 'px,0) scale(' + l3Scale + ')';
    layer3.style.opacity = l3Opacity;

    /* Background layer 0 */
    bgLayer0.style.transform = 'translate3d(0,' + s1BaseY + 'px,0)';
    bgLayer0.style.opacity = 1 - mapRange(progress, 0.72, 0.96, 0, 1);

    /* Hero text */
    var heroRaw = mapRange(progress, 0.0, 0.24, 0, 1);
    var heroP = easeOutCubic(heroRaw);
    var heroScale = 0.65 + heroP * 0.45;
    var heroOpacity = Math.min(
      mapRange(progress, 0.0, 0.03, 0, 1),
      1 - mapRange(progress, heroFadeStart, heroFadeEnd, 0, 1)
    );
    heroText.style.zIndex = progress > 0.08 ? 24 : 19;
    heroText.style.transform = 'translate3d(0,' + s1BaseY + 'px,0) scale(' + heroScale + ')';
    heroText.style.opacity = heroOpacity;

    /* Crest logo */
    if (crestLogo) {
      var crestOpacity;
      if (isMobileView) {
        crestOpacity = Math.min(
          mapRange(progress, 0.04, 0.12, 0, 1),
          1 - mapRange(progress, 0.38, 0.54, 0, 1)
        );
      } else {
        crestOpacity = 1 - mapRange(progress, 0.48, 0.64, 0, 1);
      }
      crestLogo.style.opacity = crestOpacity;
      crestLogo.style.transform = 'translate3d(0,' + s1BaseY + 'px,0)';
    }

    /* Scene 1 bottom fade */
    scene1Fade.style.opacity = mapRange(progress, isMobileView ? 0.48 : 0.56, isMobileView ? 0.72 : 0.82, 0, 1);
    scene1Fade.style.transform = 'translate3d(0,' + s1BaseY + 'px,0)';

    /* Transition divider */
    transitionBar1.style.opacity = mapRange(progress, isMobileView ? 0.54 : 0.6, isMobileView ? 0.74 : 0.82, 0, 1);
    transitionBar1.style.transform = 'translate3d(0,' + (vh * 1.02 + s1BaseY) + 'px,0)';

    /* ═══════════════════════════
       STICKY CTA
       ═══════════════════════════ */
    if (stickyCta) {
      var afterHeroPeak = progress > (isMobileView ? 0.08 : 0.12) && progress < 1;
      var pastParallax = relativeScroll > scrollHeight;

      var waitlistVisible = false;
      if (waitlistSection) {
        var rect = waitlistSection.getBoundingClientRect();
        waitlistVisible = rect.top < vh && rect.bottom > 0;
      }

      if ((afterHeroPeak || pastParallax) && !waitlistVisible) {
        stickyCta.classList.add('is-visible');
      } else {
        stickyCta.classList.remove('is-visible');
      }
    }
  }

  /* ══════════════════════════════════════════════
     ANIMATION LOOP
     - Lerps toward target scroll for smooth motion
     - Handles snap animations with same lerp
     - Skips frames when nothing has changed
     ══════════════════════════════════════════════ */
  function animate() {
    /* Lerp current scroll toward target */
    var prevScroll = currentScroll;
    currentScroll = lerp(currentScroll, targetScroll, LERP_FACTOR);

    /* Only render when scroll position actually changed */
    var scrollDelta = currentScroll - prevScroll;
    if (scrollDelta > 0.3 || scrollDelta < -0.3 || needsUpdate) {
      var relativeScroll = currentScroll - containerTop;
      render(relativeScroll);
      needsUpdate = false;
    }

    requestAnimationFrame(animate);
  }

  /* Kick off — initial render + loop */
  render(currentScroll - containerTop);
  requestAnimationFrame(animate);
})();

(function () {
  var closedText = 'Waitlist is closed for The Battle of Kurukshetra';

  function updateAnnouncement() {
    var announcement = document.querySelector('.viraasat-feature-video__announcement');
    if (!announcement) return;
    announcement.textContent = closedText;
  }

  updateAnnouncement();
  document.addEventListener('DOMContentLoaded', updateAnnouncement);
  window.addEventListener('load', updateAnnouncement);
  window.setTimeout(updateAnnouncement, 250);
  window.setTimeout(updateAnnouncement, 900);
  window.setTimeout(updateAnnouncement, 1800);
})();
